import React, { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { getSeason } from "../lib/season";
import { makeCanopy } from "../lib/textures";
import { useStore } from "../store";

const GREENS = ["#3f6b2e", "#4c7a34", "#345c28", "#57853c", "#2e5322", "#436e2c"];

// Hard-coded river / harbour no-spawn mask (Hudson, East River, Upper Bay / Liberty Island).
function inWater(x, z) {
  if (x > -900 && x < -360 && z > -500 && z < 1000) return true; // Hudson channel (to shoreline)
  if (x > 470 && x < 1150 && z > -1000 && z < 1000) return true; // East River channel
  if (x < 470 && z > 460) return true; // Upper Bay / harbour (south of Battery)
  return false;
}

// Street canopy: Battery Park esplanade + perimeter streets. Layered faceted
// foliage (icosahedron crowns w/ leaf texture) reads as full oak trees, not lollipops.
export default function StreetTrees() {
  const season = useMemo(() => getSeason(), []);
  const leaf = useMemo(() => makeCanopy(), []);
  const trunkRef = useRef();
  const canopyRef = useRef();
  const crownRef = useRef();

  const trees = useMemo(() => {
    // Citywide oak scatter (~2,600 instances) across the Manhattan grid + waterfront parks.
    let s = 71237;
    const rnd = () => {
      s = (s * 1103515245 + 12345) % 2147483648;
      return s / 2147483648;
    };
    const arr = [];
    const target = 2600;
    // dense Hudson waterfront esplanade rows
    for (let z = -300; z <= 900; z += 14) {
      const a = [-402 + rnd() * 10, z, 0.9 + rnd() * 0.4];
      const b = [-380 + rnd() * 8, z + 7, 0.85 + rnd() * 0.4];
      if (!inWater(a[0], a[1])) arr.push(a);
      if (!inWater(b[0], b[1])) arr.push(b);
    }
    let attempts = 0;
    while (arr.length < target && attempts < target * 8) {
      attempts++;
      const ang = rnd() * Math.PI * 2;
      const rad = 110 + rnd() * 1300;
      const x = Math.cos(ang) * rad;
      const z = Math.sin(ang) * rad - 40;
      // keep the memorial superblock mostly clear (a few plaza trees only)
      if (Math.abs(x + 30) < 150 && Math.abs(z + 30) < 160 && rnd() > 0.15) continue;
      if (inWater(x, z)) continue; // strict river/harbour mask — trees only on land
      arr.push([x, z, 0.8 + rnd() * 0.5]);
    }
    return arr;
  }, []);

  useLayoutEffect(() => {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const col = new THREE.Color();
    trees.forEach((t, i) => {
      const s = t[2];
      m.compose(new THREE.Vector3(t[0], 3 * s, t[1]), q, new THREE.Vector3(s, s, s));
      trunkRef.current?.setMatrixAt(i, m);
      q.setFromEuler(new THREE.Euler(0, i * 1.7, 0));
      m.compose(new THREE.Vector3(t[0], 6.6 * s, t[1]), q, new THREE.Vector3(3.4 * s, 3.9 * s, 3.4 * s));
      canopyRef.current?.setMatrixAt(i, m);
      m.compose(new THREE.Vector3(t[0] + s * 0.6, 8.8 * s, t[1] - s * 0.4), q, new THREE.Vector3(2.3 * s, 2.5 * s, 2.3 * s));
      crownRef.current?.setMatrixAt(i, m);
      col.set(GREENS[i % GREENS.length]);
      canopyRef.current?.setColorAt(i, col);
      crownRef.current?.setColorAt(i, col);
      q.identity();
    });
    [trunkRef, canopyRef, crownRef].forEach((r) => {
      if (r.current) r.current.instanceMatrix.needsUpdate = true;
      if (r.current?.instanceColor) r.current.instanceColor.needsUpdate = true;
    });
  }, [trees]);

  // Perf-guard LOD: render fewer trees on weak devices (chase 90 FPS in VR).
  useFrame(() => {
    const q = THREE.MathUtils.clamp(useStore.getState().qualityScale, 0.3, 1);
    const n = Math.max(1, Math.floor(trees.length * q));
    [trunkRef, canopyRef, crownRef].forEach((r) => {
      if (r.current) r.current.count = n;
    });
  });

  return (
    <group>
      <instancedMesh ref={trunkRef} args={[null, null, trees.length]} castShadow>
        <cylinderGeometry args={[0.28, 0.5, 6, 6]} />
        <meshStandardMaterial color="#4f3f28" roughness={0.95} />
      </instancedMesh>
      {!season.bare && (
        <>
          <instancedMesh ref={canopyRef} args={[null, null, trees.length]} castShadow>
            <icosahedronGeometry args={[1, 1]} />
            <meshStandardMaterial map={leaf} roughness={0.85} flatShading />
          </instancedMesh>
          <instancedMesh ref={crownRef} args={[null, null, trees.length]} castShadow>
            <icosahedronGeometry args={[1, 1]} />
            <meshStandardMaterial map={leaf} roughness={0.85} flatShading />
          </instancedMesh>
        </>
      )}
    </group>
  );
}
